import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { apiCall } from '../utils/api';
import { useToast } from '../hooks/use-toast';
import { MapPin, AlertTriangle, RefreshCw, Filter, Calendar, ChevronLeft, Layers, Download, FileImage, FileText } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Risk level weights for heatmap intensity
const RISK_WEIGHTS = {
  critical: 1.0,
  high: 0.75,
  medium: 0.5,
  low: 0.25,
  unknown: 0.1
};

// Custom marker icons for different risk levels
const createCustomIcon = (riskLevel) => {
  const colors = {
    critical: '#ef4444',
    high: '#f97316',
    medium: '#eab308',
    low: '#22c55e',
    unknown: '#6b7280'
  };
  
  const color = colors[riskLevel] || colors.unknown;
  
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

// Heatmap layer component
const HeatmapLayer = ({ scans, visible }) => {
  const map = useMap();
  const heatLayerRef = useRef(null);

  useEffect(() => {
    if (!visible) {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
      return;
    }

    if (scans.length === 0) return;

    // Create heatmap data points with weights based on risk level
    const heatData = scans.map(scan => [
      scan.geolocation.latitude,
      scan.geolocation.longitude,
      RISK_WEIGHTS[scan.overall_risk] || 0.1
    ]);

    // Remove existing heatmap layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
    }

    // Create new heatmap layer
    heatLayerRef.current = L.heatLayer(heatData, {
      radius: 25,
      blur: 15,
      maxZoom: 17,
      gradient: {
        0.0: 'green',
        0.25: 'yellow',
        0.5: 'orange',
        0.75: 'red',
        1.0: 'darkred'
      }
    }).addTo(map);

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
      }
    };
  }, [map, scans, visible]);

  return null;
};

// Component to fit map bounds to markers
const FitBounds = ({ scans }) => {
  const map = useMap();
  
  useEffect(() => {
    if (scans.length > 0) {
      const bounds = L.latLngBounds(
        scans.map(scan => [
          scan.geolocation.latitude,
          scan.geolocation.longitude
        ])
      );
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [scans, map]);
  
  return null;
};

const ScanMapView = ({ onClose }) => {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterRisk, setFilterRisk] = useState('all');
  const [filterDays, setFilterDays] = useState('90');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showMarkers, setShowMarkers] = useState(true);
  const [exporting, setExporting] = useState(false);
  const mapContainerRef = useRef(null);
  const { toast } = useToast();

  const fetchGeotaggedScans = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('days', filterDays);
      if (filterRisk !== 'all') {
        params.append('risk_level', filterRisk);
      }
      
      const data = await apiCall(`/api/ai-hazard-scan/geotagged-scans?${params.toString()}`);
      setScans(data.scans || []);
      
      if (data.scans?.length === 0) {
        toast({ 
          title: 'No Geotagged Scans', 
          description: 'No scans with GPS data found for the selected filters.' 
        });
      }
    } catch (err) {
      console.error('Failed to fetch geotagged scans:', err);
      setError(err.message);
      toast({ title: 'Load Failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filterRisk, filterDays, toast]);

  useEffect(() => {
    fetchGeotaggedScans();
  }, [fetchGeotaggedScans]);

  const getRiskBadgeClass = (risk) => {
    const classes = {
      critical: 'bg-red-100 text-red-700 border-red-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      low: 'bg-green-100 text-green-700 border-green-300'
    };
    return classes[risk] || 'bg-gray-100 text-gray-700';
  };

  // Export map as PNG image
  const exportAsImage = async () => {
    if (!mapContainerRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(mapContainerRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const link = document.createElement('a');
      link.download = `hazard-map-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast({ title: 'Export Complete', description: 'Map exported as PNG image' });
    } catch (err) {
      console.error('Export error:', err);
      toast({ title: 'Export Failed', description: err.message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  // Export map as PDF
  const exportAsPDF = async () => {
    if (!mapContainerRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(mapContainerRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height + 100]
      });
      
      // Add title
      pdf.setFontSize(24);
      pdf.setTextColor(30, 41, 59);
      pdf.text('Hazard Scan Map Report', 40, 40);
      
      // Add metadata
      pdf.setFontSize(12);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 40, 60);
      pdf.text(`Total Scans: ${scans.length} | Filter: ${filterRisk === 'all' ? 'All Risks' : filterRisk} | Period: Last ${filterDays} days`, 40, 78);
      
      // Add map image
      pdf.addImage(imgData, 'PNG', 0, 100, canvas.width, canvas.height);
      
      // Add legend
      const legendY = canvas.height + 120;
      pdf.setFontSize(10);
      pdf.text('Legend:', 40, legendY);
      pdf.setFillColor(239, 68, 68);
      pdf.circle(55, legendY + 15, 5, 'F');
      pdf.text('Critical', 65, legendY + 18);
      pdf.setFillColor(249, 115, 22);
      pdf.circle(115, legendY + 15, 5, 'F');
      pdf.text('High', 125, legendY + 18);
      pdf.setFillColor(234, 179, 8);
      pdf.circle(165, legendY + 15, 5, 'F');
      pdf.text('Medium', 175, legendY + 18);
      pdf.setFillColor(34, 197, 94);
      pdf.circle(235, legendY + 15, 5, 'F');
      pdf.text('Low', 245, legendY + 18);
      
      pdf.save(`hazard-map-${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast({ title: 'Export Complete', description: 'Map exported as PDF report' });
    } catch (err) {
      console.error('PDF export error:', err);
      toast({ title: 'Export Failed', description: err.message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  // Default center (US)
  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Scan Map View
            </CardTitle>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* View Toggle */}
            <div className="flex items-center border rounded-lg overflow-hidden">
              <Button 
                size="sm" 
                variant={showMarkers && !showHeatmap ? "default" : "ghost"}
                onClick={() => { setShowMarkers(true); setShowHeatmap(false); }}
                className="rounded-none"
              >
                <MapPin className="w-4 h-4 mr-1" />
                Markers
              </Button>
              <Button 
                size="sm" 
                variant={showHeatmap ? "default" : "ghost"}
                onClick={() => { setShowHeatmap(true); setShowMarkers(false); }}
                className="rounded-none"
              >
                <Layers className="w-4 h-4 mr-1" />
                Heatmap
              </Button>
              <Button 
                size="sm" 
                variant={showMarkers && showHeatmap ? "default" : "ghost"}
                onClick={() => { setShowMarkers(true); setShowHeatmap(true); }}
                className="rounded-none"
              >
                Both
              </Button>
            </div>

            {/* Risk Filter */}
            <Select value={filterRisk} onValueChange={setFilterRisk}>
              <SelectTrigger className="w-32">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risks</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Date Filter */}
            <Select value={filterDays} onValueChange={setFilterDays}>
              <SelectTrigger className="w-32">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>

            {/* Export Buttons */}
            <div className="flex items-center border rounded-lg overflow-hidden">
              <Button 
                size="sm" 
                variant="ghost"
                onClick={exportAsImage}
                disabled={exporting || scans.length === 0}
                title="Export as PNG"
              >
                <FileImage className="w-4 h-4" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={exportAsPDF}
                disabled={exporting || scans.length === 0}
                title="Export as PDF"
              >
                <FileText className="w-4 h-4" />
              </Button>
            </div>
            
            <Button variant="outline" size="sm" onClick={fetchGeotaggedScans} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        
        {/* Stats bar */}
        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 flex-wrap">
          <span>{scans.length} geotagged scan{scans.length !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500"></div> Critical
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div> High
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div> Medium
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div> Low
            </span>
          </div>
          {showHeatmap && (
            <span className="text-purple-600 font-medium">Heatmap: Intensity = Risk Level</span>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {error ? (
          <div className="flex items-center justify-center h-96 bg-gray-50">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-2" />
              <p className="text-gray-600">{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchGeotaggedScans}>
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <div ref={mapContainerRef} className="h-[500px] relative">
            {loading && (
              <div className="absolute inset-0 bg-white/70 z-[1000] flex items-center justify-center">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            )}
            {exporting && (
              <div className="absolute inset-0 bg-white/70 z-[1000] flex items-center justify-center">
                <div className="text-center">
                  <Download className="w-8 h-8 text-blue-600 animate-bounce mx-auto" />
                  <p className="text-sm text-gray-600 mt-2">Exporting...</p>
                </div>
              </div>
            )}
            
            <MapContainer
              center={defaultCenter}
              zoom={defaultZoom}
              className="h-full w-full"
              style={{ zIndex: 1 }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {scans.length > 0 && <FitBounds scans={scans} />}
              
              {/* Heatmap Layer */}
              <HeatmapLayer scans={scans} visible={showHeatmap} />
              
              {/* Markers */}
              {showMarkers && scans.map((scan) => (
                <Marker
                  key={scan.id}
                  position={[scan.geolocation.latitude, scan.geolocation.longitude]}
                  icon={createCustomIcon(scan.overall_risk)}
                >
                  <Popup className="scan-popup" maxWidth={300}>
                    <div className="p-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-800">{scan.scan_number}</span>
                        <Badge className={getRiskBadgeClass(scan.overall_risk)}>
                          {scan.overall_risk?.toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="space-y-1 text-sm">
                        <p><strong>Location:</strong> {scan.location || 'Unknown'}</p>
                        <p><strong>Project:</strong> {scan.project_name || 'N/A'}</p>
                        <p><strong>Scan Type:</strong> {scan.scan_type}</p>
                        <p><strong>Hazards:</strong> {scan.hazards_count}</p>
                        <p><strong>Date:</strong> {new Date(scan.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-gray-500">
                          GPS: {scan.geolocation.latitude.toFixed(5)}, {scan.geolocation.longitude.toFixed(5)}
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScanMapView;
